import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import DoNotDisturbAltOutlinedIcon from '@mui/icons-material/DoNotDisturbAltOutlined';
import { PromoCode } from 'common/contracts';
import { Coupon, Offer } from 'common/contracts';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import utc from 'dayjs/plugin/utc';
import { EStatusLabel } from 'design-system/StatusLabel/types';
import { EToasterStatus, showToast } from 'utils/showToast';
import * as yup from 'yup';

import {
  CouponFormValues,
  EExpirationMode,
  UpsertCouponDto,
  UpsertPromoCodeDto
} from './types';

dayjs.extend(utc);
dayjs.extend(isSameOrAfter);

export const STATUS_TO_ICON_MAP = {
  [EStatusLabel.SUCCESS]: <CheckCircleOutlineOutlinedIcon />,
  [EStatusLabel.FAILED]: <DoNotDisturbAltOutlinedIcon />,
  [EStatusLabel.PENDING]: <AccessTimeOutlinedIcon />
};

export function isDatePassed(timestamp: string) {
  return dayjs().isAfter(dayjs(timestamp));
}

export const generatePromoCodesDto = (
  promoCodes: PromoCode[],
  couponId?: string
): UpsertPromoCodeDto[] => {
  return promoCodes.map(({ name, maxRedemptions, active, id, fresh }) => {
    const data: UpsertPromoCodeDto = {
      name,
      maxRedemptions,
      active,
      couponId
    };

    if (id && !fresh) {
      data.id = id;
    }

    return data;
  });
};

export const generateCouponDto = (
  values: CouponFormValues,
  neverExpires: boolean
): UpsertCouponDto => {
  return {
    name: values.name,
    active: values.active,
    discountPercentage: Number(values.discountPercentage),
    maxRedemptionsPerCustomer: values.maxRedemptionsPerCustomer
      ? Number(values.maxRedemptionsPerCustomer)
      : null,
    startsAt: values.startsAt || null,
    expiredBy:
      values.expirationMode === EExpirationMode.TEMPORARY ? values.expiredBy : null,
    firstTimePurchase: values.firstTimePurchase,
    supportedOffersBySku: values.supportedOffersBySku,
    allowedPlayers: values.allowedPlayers
  };
};

export const checkUniqueness = (
  newName: string = '',
  coupons: Coupon[] | undefined,
  currentCouponName: string = '',
  isEdit: boolean
): boolean => {
  const couponsNames = coupons?.map((coupon) => coupon.name);
  if (isEdit) {
    return !couponsNames
      ?.filter((name) => name !== currentCouponName)
      .includes(newName);
  }
  return !couponsNames?.includes(newName);
};

export const getOffersForSelect = (
  offersMap: { bundles?: Offer[]; promotions?: Offer[]; popups?: Offer[] },
  supportedOffersByType: string[] = ['bundles', 'promotions', 'popups']
) => {
  const offers = [];
  if (offersMap.bundles && supportedOffersByType.includes('bundles')) {
    offers.push(
      ...offersMap.bundles.map((offer) => ({
        content: offer.name,
        key: offer.publisherOfferId,
        value: offer.name
      }))
    );
  }
  if (offersMap.promotions && supportedOffersByType.includes('promotions')) {
    offers.push(
      ...offersMap.promotions.map((offer) => ({
        content: offer.name,
        key: offer.publisherOfferId,
        value: offer.name
      }))
    );
  }
  if (offersMap.popups && supportedOffersByType.includes('popups')) {
    offers.push(
      ...offersMap.popups.map((offer) => ({
        content: offer.name,
        key: offer.publisherOfferId,
        value: offer.name
      }))
    );
  }
  return offers;
};

export const handleUpsertSuccess = async ({
  getSingleCoupon,
  helpers,
  isEdit
}: {
  getSingleCoupon: any;
  helpers: any;
  isEdit: boolean;
}) => {
  await getSingleCoupon.refetch();
  if (getSingleCoupon.data) {
    helpers.setValues({
      ...getSingleCoupon.data,
      promoCodes: (getSingleCoupon.data.promoCodes || []).map(
        (promoCode: any) => ({
          ...promoCode,
          fresh: false
        })
      )
    });
  }
  showToast({
    message: `Coupon ${isEdit ? 'updated' : 'created'} successfully`,
    status: EToasterStatus.SUCCESS
  });
};

export const handleUpsertError = (error: any, isEdit: boolean) => {
  const serverError = error?.response?.data?.message;
  showToast({
    message: serverError || `Error ${isEdit ? 'updating' : 'creating'} coupon`,
    status: EToasterStatus.ERROR
  });
};

export const deriveExpirationMode = (
  startsAt?: string | null,
  expiredBy?: string | null
): EExpirationMode => {
  return expiredBy
    ? EExpirationMode.TEMPORARY
    : EExpirationMode.PERMANENT;
};

export const applyExpirationModeChange = (
  mode: EExpirationMode,
  setFieldValue: (field: string, value: any) => void,
  setFieldTouched: (field: string, isTouched?: boolean | undefined) => void,
  setNeverExpires: (value: boolean) => void,
  isEdit: boolean = false
) => {
  setNeverExpires(false);
  if (!isEdit) {
    setFieldValue('startsAt', null);
    setFieldValue('expiredBy', null);
    setFieldTouched('startsAt', false);
    setFieldTouched('expiredBy', false);
  }
};

export const buildCouponValidationSchema = (params: {
  checkUniqueness: (
    newName: string,
    coupons: Coupon[] | undefined,
    currentCouponName: string,
    isEdit: boolean
  ) => boolean;
  getCouponsData: Coupon[] | undefined;
  currentCouponName: string;
  isEdit: boolean;
  isCheckoutUser: boolean;
  initialStartsAt?: string | null;
  initialExpiredBy?: string | null;
}) => {
  const {
    checkUniqueness,
    getCouponsData,
    currentCouponName,
    isEdit,
    isCheckoutUser,
    initialStartsAt,
    initialExpiredBy
  } = params;

  const isDateUnchanged = (value: Date | null | undefined, initialValue: string | null | undefined): boolean => {
    if (!value && !initialValue) return true;
    if (!value || !initialValue) return false;
    return dayjs(value).isSame(dayjs(initialValue));
  };

  return yup.object().shape({
    name: yup
      .string()
      .required('Coupon name is required')
      .test('uniqueness', 'Coupon name already exists', (value) => {
        return checkUniqueness(
          value || '',
          getCouponsData,
          currentCouponName || '',
          isEdit
        );
      }),
    active: yup.boolean().required('Coupon status is required'),
    discountPercentage: yup
      .number()
      .integer('Discount percentage must be a whole number')
      .min(1, 'Discount percentage must be at least 1')
      .max(99, 'Discount percentage must be at most 99')
      .required('Discount percentage is required'),
    maxRedemptionsPerCustomer: yup
      .number()
      .min(0, 'Max redemptions per customer must be greater than or equal to 1')
      .notRequired(),
    startsAt: yup
      .date()
      .nullable()
      .test(
        'is-future-date',
        'Start date must be today or a future date',
        (value) => {
          if (!value) return true;
          if (isEdit && isDateUnchanged(value, initialStartsAt)) return true;
          return dayjs(value).utc().isSameOrAfter(dayjs().utc().startOf('day'));
        }
      )
      .when('expirationMode', {
        is: (mode: EExpirationMode) => mode === EExpirationMode.TEMPORARY,
        then: (schema) => schema
          .required('Date range is required')
          .test(
            'range-complete',
            'Date range is required',
            function (value) {
              const { expiredBy } = this.parent;
              if (value && !expiredBy) return false;
              return true;
            }
          ),
        otherwise: (schema) => schema.required('Start date is required')
      }),
    expiredBy: yup
      .date()
      .nullable()
      .test(
        'is-future-date',
        'Expiries on date must in the future',
        (value) => {
          if (!value) return true;
          if (isEdit && isDateUnchanged(value, initialExpiredBy)) return true;
          return dayjs(value).utc().isSameOrAfter(dayjs().utc().startOf('day'));
        }
      )
      .when('startsAt', (startValue: unknown, schema: any) => {
        if (!startValue) return schema;
        const parsedStart = dayjs(startValue as unknown as string);
        return parsedStart.isValid()
          ? schema.test(
              'is-after-start',
              'Expiry date must be after start date',
              (value: Date | undefined) => {
                if (!value) return true;
                return dayjs(value).isAfter(parsedStart);
              }
            )
          : schema;
      })
      .when('expirationMode', {
        is: (mode: EExpirationMode) => mode === EExpirationMode.TEMPORARY,
        then: (schema) => schema.required('Expiry date is required')
      }),
    firstTimePurchase: yup.boolean(),
    ...(isCheckoutUser
      ? {}
      : {
          supportedOffersBySku: yup.array().of(yup.string())
        }),
    promoCodes: yup
      .array()
      .of(
        yup.object().shape({
          name: yup.string().required('Promo code name is required'),
          maxRedemptions: yup
            .number()
            .min(0, 'Max redemptions must be greater than or equal to 0')
            .required('Max redemptions is required')
        })
      )
      .min(1, 'At least one promo code must be added')
  });
};
